import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import App from './App.vue'
import store from './store'
import routes from './routes'
import 'bootstrap/dist/css/bootstrap.min.css'
// 必须与各组件中 `import { X } from 'bootstrap'` 解析到同一个模块（bootstrap.esm.js），
// 否则会加载两份 Bootstrap，dropdown 等 data-api 监听器重复注册导致菜单一打开就被立刻关闭。
import 'bootstrap'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import '@fortawesome/fontawesome-free/css/all.css'
import logger from '@/utils/logger'

import axios from 'axios'

// Configure axios defaults
// 禁止修改此配置！！！
axios.defaults.baseURL = process.env.VUE_APP_API_URL || '/api'
axios.defaults.headers.common['Content-Type'] = 'application/json'

// Add axios interceptor for auth token with debug logging
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token')
    const requestUrl = config.url

    // Log URL and token presence for debugging
    if (requestUrl?.includes('/customers')) {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Ensure Authorization header is always present for authenticated requests
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => {
    logger.error('[DEBUG] Axios request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Add axios response interceptor for error handling and redirect handling
axios.interceptors.response.use(
  response => response,
  async error => {
    // Log the error for debugging
    logger.error('Axios response error:', {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      baseURL: error.config?.baseURL,
      location: error.response?.data?.location
    })

    // Handle 308 redirect specifically to preserve authentication headers
    if (error.response?.status === 308 && error.response?.headers?.location) {
      logger.warn('Handling 308 redirect with authentication headers preservation')

      // Get the original request config
      const originalConfig = error.config
      const location = error.response.headers.location

      // Extract authentication headers from original request
      const originalHeaders = originalConfig.headers || {}

      // Ensure we have the updated authorization header
      const token = localStorage.getItem('access_token')
      const authorizationHeader = token ? `Bearer ${token}` : originalHeaders.Authorization

      logger.log('Retrying request with preserved auth headers:', {
        url: location,
        originalHeaders: Object.keys(originalHeaders),
        hasAuth: !!authorizationHeader
      })

      try {
        // Retry the request with the new location and preserved auth headers
        const response = await axios({
          ...originalConfig,
          url: location,
          headers: {
            ...originalHeaders,
            Authorization: authorizationHeader
          }
        })

        logger.log('Successfully handled 308 redirect with preserved auth')
        return response
      } catch (retryError) {
        logger.error('Failed to handle 308 redirect retry:', retryError)

        // If it's a 401 error after retry, clear auth and redirect to login
        if (retryError.response?.status === 401) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }

        return Promise.reject(retryError)
      }
    }

    if (error.response?.status === 401) {
      // Skip redirect for login requests to allow error display
      const isLoginRequest = error.config?.url?.includes('/auth/login')
      if (isLoginRequest) {
        return Promise.reject(error)
      }

      // Token expired or invalid
      logger.warn('Authentication required - redirecting to login')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    } else if (error.response?.status === 403) {
      logger.warn('Access forbidden - insufficient permissions')
    }
    return Promise.reject(error)
  }
)

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('access_token')
  const user = localStorage.getItem('user')

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token || !user) {
      next({ name: 'login' })
      return
    }

    // Check if route requires admin privileges
    if (to.matched.some(record => record.meta.requiresAdmin)) {
      try {
        const userData = JSON.parse(user)
        const isAdmin = userData.role_id === 3
        if (!isAdmin) {
          // Redirect to dashboard with error notification
          logger.warn('Non-admin user attempted to access admin route:', to.path)

          // Show toast notification for unauthorized access
          // We'll create a temporary notification since store might not be available here
          setTimeout(() => {
            const toast = app.config.globalProperties.$toast
            if (toast) {
              toast.warning('您没有权限访问用户管理功能', {
                position: 'top-right',
                timeout: 3000,
                closeOnClick: true,
                pauseOnFocusLoss: true,
                pauseOnHover: true,
                draggable: true,
                draggablePercent: 0.6,
                showCloseButtonOnHover: false,
                hideProgressBar: false,
                closeButton: 'button',
                icon: true,
                rtl: false
              })
            }
          }, 100)

          next({ name: 'dashboard' })
          return
        }
      } catch (error) {
        logger.error('Error parsing user data for admin check:', error)
        next({ name: 'login' })
        return
      }
    }

    // requiresDemo 表示仅超级管理员可访问（“演示用户”是历史命名，
    // 实际为 demonstration_user 白名单，不是演示/只读账号）
    if (to.matched.some(record => record.meta.requiresDemo)) {
      try {
        const response = await fetch('/api/users/can-switch-role', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const result = await response.json()
        if (!response.ok || !result.can_switch) {
          logger.warn('Non-demo user attempted to access demo route:', to.path)
          setTimeout(() => {
            const toast = app.config.globalProperties.$toast
            if (toast) {
              toast.warning('仅演示用户可访问登录安全审计', {
                position: 'top-right',
                timeout: 3000,
                closeOnClick: true,
                pauseOnFocusLoss: true,
                pauseOnHover: true,
                draggable: true,
                draggablePercent: 0.6,
                showCloseButtonOnHover: false,
                hideProgressBar: false,
                closeButton: 'button',
                icon: true,
                rtl: false
              })
            }
          }, 100)

          next({ name: 'dashboard' })
          return
        }
      } catch (error) {
        logger.error('Error checking demonstration user permission:', error)
        next({ name: 'dashboard' })
        return
      }
    }

    next()
  } else {
    next()
  }
})

const pinia = createPinia()

const app = createApp(App)

// Global properties
app.config.globalProperties.$axios = axios

app.use(pinia)
app.use(router)
app.use(store)
app.use(Toast, {
  transition: 'Vue-Toastification__fade',
  maxToasts: 5,
  newestOnTop: true,
  position: 'top-right',
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false
})

app.mount('#app')